import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { FinancialCompanyProxyService } from '../proxies/finance/financial-company.proxy.service';

/**
 * Resolves a Site id to the FinancialCompany id that owns it — the UI mirror of the Finance API's
 * SiteCompanyResolver. FinancialAccounts are scoped by financial-company id (NOT site id), so any
 * GL-account lookup must map the selected site through here first. Cached per site id (companies are
 * static reference data); in-flight requests are shared.
 */
@Injectable({ providedIn: 'root' })
export class SiteCompanyService {
  private readonly companyProxy = inject(FinancialCompanyProxyService);
  private readonly cache = new Map<string, Observable<string | null>>();

  /** Returns the FinancialCompany id for a site, or null if none is mapped. Cached. */
  public getFinancialCompanyId(siteId: string | null | undefined): Observable<string | null> {
    if (!siteId) return of(null);

    let resolved = this.cache.get(siteId);
    if (!resolved) {
      resolved = this.companyProxy.getBySite(siteId).pipe(
        map((response) => response.data?.id ?? null),
        catchError(() => of(null)),
        shareReplay(1),
      );
      this.cache.set(siteId, resolved);
    }
    return resolved;
  }
}
